import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { HomeIcon } from '@eriksdigital/atomic-ui/components/Icons';
import styled, { css } from 'styled-components';

import { ReactComponent as DocumentsIcon } from 'assets/icons/documents.svg';
import { ReactComponent as WorkOrdersIcon } from 'assets/icons/work_orders.svg';
import { translationString } from 'locales/translation';
import { useInjectReducer } from 'utils/redux-injectors';
import routeHelpers from 'utils/route-helpers';
import { matchMedia, mediaQuery } from 'utils/mediaQuery';
import MenuAssets from './components/MenuAssets';
import MenuEquipment from './components/MenuEquipment';
import MenuItem from './components/MenuItem';
import MenuSettings from './components/MenuSettings';
import { selectMenuIsOpen, selectSubMenuIsOpen } from './selectors';
import { actions, reducer, sliceKey } from './slice';
import CreateEquipmentTypeModal from './components/CreateEquipmentTypeModal';
import {
  selectCustomerHasAssetsMenu,
  selectCustomerHasEquipmentsAndDashboardMenus,
  selectSelectedCustomer,
  selectSelectedLocation,
} from '../CustomerLocation/selectors';

function SideMenu() {
  useInjectReducer({ key: sliceKey, reducer: reducer });

  const isMenuOpen = useSelector(selectMenuIsOpen);
  const isSubMenuOpen = useSelector(selectSubMenuIsOpen());
  const customerId = useSelector(selectSelectedCustomer);
  const customerHasAssetsMenu = useSelector(selectCustomerHasAssetsMenu);
  const customerHasEquipmentsAndDashboardMenus = useSelector(
    selectCustomerHasEquipmentsAndDashboardMenus,
  );
  const locationId = useSelector(selectSelectedLocation);

  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.close());
  }, [location, dispatch]);

  useEffect(() => {
    if (matchMedia.phoneLarge.matches) {
      return;
    }

    if (isMenuOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden');
      document.body.style.setProperty('overflow', 'hidden');
    } else {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
    }
  }, [isMenuOpen]);

  return (
    <SideMenuContainer isMenuOpen={isMenuOpen} isSubMenuOpen={isSubMenuOpen}>
      <Menu>
        {locationId && customerHasEquipmentsAndDashboardMenus && (
          <MenuItem
            to={routeHelpers.buildMainDashboardRoute(customerId, locationId)}
            icon={HomeIcon}
            label={translationString('Dashboard')}
          />
        )}

        {customerHasAssetsMenu && <MenuAssets />}
        {customerHasEquipmentsAndDashboardMenus && <MenuEquipment />}
        <MenuItem
          to={routeHelpers.buildDashboardTasksRoute(customerId, locationId)}
          icon={WorkOrdersIcon}
          label={translationString('Tasks')}
        />
        {/* <MenuItem to="/" icon={QuoteIcon} label={translationString('Quotes')} /> */}
        <MenuItem
          to={routeHelpers.buildDashboardAttachmentsRoute(
            customerId,
            locationId,
          )}
          icon={DocumentsIcon}
          label={translationString('Attachments.Attachments')}
        />
        <MenuSettings />
      </Menu>
      <CreateEquipmentTypeModal />
    </SideMenuContainer>
  );
}

export default withRouter(SideMenu);

const SideMenuContainer = styled.nav<{
  isMenuOpen: boolean;
  isSubMenuOpen: boolean;
}>`
  position: fixed;
  top: 40px;
  bottom: 0;
  left: 0;
  width: 100vw;
  max-width: 0;
  background-color: ${({ theme }) => theme.colors.default.blue1};
  z-index: 2;
  transition: max-width 300ms;

  ${({ isMenuOpen }) =>
    isMenuOpen &&
    css`
      max-width: 100vw;
    `}

  ${({ isSubMenuOpen }) =>
    isSubMenuOpen &&
    css`
      max-width: 0;
    `}

  ${mediaQuery.phoneLarge} {
    top: 80px;
    width: 96px;
    max-width: unset;
  }
`;

const Menu = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  overflow: hidden;
`;
